// TomorrowPlanCard — shows tomorrow's target dose (pre-computed by the
// taper algorithm and stored on `taper_plan`) plus an optional clock-time
// schedule scaled from the user's own dose history over the last 14 days.
//
// Everything in this card is deterministic. Targets come from the user's
// own plan. Times come from their own logs. No AI involvement. The card
// always shows a disclaimer reminding the user this is descriptive of
// their plan + rhythm, not medical advice.
//
// Self-hides when the API returns 404 (no active plan, or past plan end)
// — we don't want to show a broken card to a brand-new user.
//
// All styling lives in `components.css` under `.tomorrow-plan-card__*`
// rules and the shared `.numeric-display` / `.chip-accent-soft` /
// `.chip-primary-soft` utilities. The JSX is logic-only.

import { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { Skeleton } from '../ui/Skeleton';
import { CalendarClock, Info, Pause } from 'lucide-react';
import {
  useTomorrowPlan,
  type TomorrowScheduleSlot,
} from '../../hooks/useTomorrowPlan';

export function TomorrowPlanCard() {
  const tomorrow = useTomorrowPlan();
  const [tipOpen, setTipOpen] = useState(false);

  // Close the inline tooltip on outside click / escape — keep it cheap.
  useEffect(() => {
    if (!tipOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTipOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tipOpen]);

  if (tomorrow.status === 'idle' || tomorrow.status === 'loading') {
    return (
      <Card accentLeft className="tomorrow-plan-card">
        <div className="skeleton-stack">
          <Skeleton height="1.25rem" width="40%" />
          <Skeleton height="2.5rem" width="55%" />
          <Skeleton height="0.875rem" width="70%" />
        </div>
      </Card>
    );
  }

  // 404 → hide entirely. Other errors (5xx, network) → also hide rather
  // than show a broken state on the Today screen's most prominent zone.
  if (tomorrow.status === 'error') return null;

  const data = tomorrow.data;
  const targetLabel = formatTargetMg(data.targetMg);
  const dateLabel = formatFriendlyDate(data.date);
  const hasSchedule = data.suggestedSchedule.length > 0;

  return (
    <Card accentLeft className="tomorrow-plan-card">
      <div className="tomorrow-plan-card__header">
        <Icon icon={CalendarClock} size={16} aria-hidden="true" />
        <span className="text-micro tomorrow-plan-card__eyebrow">
          Tomorrow · {dateLabel}
        </span>
        {data.isHold ? (
          <span className="text-micro chip-accent-soft">
            <Icon icon={Pause} size={12} aria-hidden="true" /> Hold
          </span>
        ) : null}
        {data.isFinalDay ? (
          <span className="text-micro chip-primary-soft">
            Final day
          </span>
        ) : null}
      </div>

      <div
        className="tomorrow-plan-card__amount-row"
        data-has-schedule={hasSchedule ? 'true' : 'false'}
      >
        <span className="numeric-display">{targetLabel}</span>
        <span className="text-caption muted">mg target</span>
      </div>

      {data.isHold && data.holdReason ? (
        <p className="text-caption muted tomorrow-plan-card__hold-reason">
          Held: {data.holdReason}
        </p>
      ) : null}

      {hasSchedule ? (
        <>
          <div className="row row--center tomorrow-plan-card__rhythm-row">
            <span className="text-caption muted">Your typical rhythm</span>
            <button
              type="button"
              aria-label="What is this based on?"
              aria-expanded={tipOpen}
              onClick={() => setTipOpen((v) => !v)}
              className="tomorrow-plan-card__info"
            >
              <Icon icon={Info} size={14} aria-hidden="true" />
            </button>
          </div>
          {tipOpen ? (
            <p role="note" className="text-caption muted tomorrow-plan-card__tip">
              Times come from your dose log over the last 14 days. Amounts split tomorrow&apos;s target across your typical times. Adjust freely — this is your taper, not a prescription.
            </p>
          ) : null}
          <ul className="tomorrow-plan-card__schedule" aria-label="Suggested dose times">
            {data.suggestedSchedule.map((slot) => (
              <ScheduleChip key={slot.time} slot={slot} />
            ))}
          </ul>
        </>
      ) : !data.isFinalDay && data.targetMg > 0 ? (
        <p className="text-caption muted tomorrow-plan-card__schedule-empty">
          Log a few days of doses to see suggested times based on your rhythm.
        </p>
      ) : null}

      <p className="text-micro tomorrow-plan-card__disclaimer">
        Your plan&apos;s target and your typical rhythm — not medical advice.
      </p>
    </Card>
  );
}

function ScheduleChip({ slot }: { slot: TomorrowScheduleSlot }) {
  return (
    <li className="tomorrow-plan-card__chip" data-confidence={slot.confidence}>
      <span className="tomorrow-plan-card__chip-time">{formatClock(slot.time)}</span>
      <span className="tomorrow-plan-card__chip-mg">~{formatTargetMg(slot.mg)} mg</span>
    </li>
  );
}

function formatTargetMg(mg: number): string {
  // Drop trailing .0 for whole-mg values; keep one decimal otherwise.
  if (Math.round(mg) === mg) return String(mg);
  return mg.toFixed(1);
}

function formatClock(hhmm: string): string {
  // 12-hour clock with AM/PM — friendlier on the schedule strip.
  const [h, m] = hhmm.split(':').map((s) => Number.parseInt(s, 10));
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  if (m === 0) return `${hour12} ${period}`;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function formatFriendlyDate(ymd: string): string {
  // 'Thu, May 15' from '2026-05-15'.
  const [y, mo, d] = ymd.split('-').map((s) => Number.parseInt(s, 10));
  const dt = new Date(Date.UTC(y, mo - 1, d));
  const weekday = dt.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  const month = dt.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
  return `${weekday}, ${month} ${d}`;
}
